import { Link, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { GeneratePage } from './GeneratePage';
import { HistoryPage } from './HistoryPage';
import { AccountPage } from './AccountPage';
import { FreeModeChat } from './FreeModeChat';
import { SocialPostsPage } from './SocialPostsPage';
import { UsageDashboard } from '../../components/UsageDashboard';

export function Dashboard(){
  return (
    <AppLayout>
      <Routes>
        <Route index element={<OverviewPage />} />
        <Route path="generate" element={<GeneratePage />} />
        <Route path="social-posts" element={<SocialPostsPage />} />
        <Route path="chat" element={<FreeModeChat />} />
        {/* <Route path="history" element={<HistoryPage />} /> */}
        <Route path="account" element={<AccountPage />} />
        <Route path="*" element={<Navigate to="." replace/>} />
      </Routes>
    </AppLayout>
  );
}

function AppLayout({children}:{children:React.ReactNode}){
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const [open,setOpen] = useState(false);
  return (
    <div className="min-h-screen flex flex-col bg-[var(--cg-bg)] text-[var(--cg-text)]">
      {/* Header melhorado */}
      <header className="sticky top-0 z-50 backdrop-blur-lg bg-[var(--cg-surface)]/80 border-b" style={{borderColor:'var(--cg-border)'}}>
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="h-16 flex items-center justify-between">
            {/* Logo e Nav */}
            <div className="flex items-center gap-8">
              <Link to="/app" className="flex items-center gap-2 group">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-brand-500 to-brand-700 flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform">
                  <i className="fa-solid fa-wand-magic-sparkles text-white text-sm"></i>
                </div>
                <span className="font-bold text-lg tracking-tight">CopyGenius</span>
              </Link>

              <nav className="hidden md:flex items-center gap-1">
                <NavLink to="/app/generate" label="Gerar" icon="fa-wand-magic-sparkles" />
                <NavLink to="/app/social-posts" label="Posts" icon="fa-images" />
                <NavLink to="/app/chat" label="Chat" icon="fa-comments" />
                <NavLink to="/app/account" label="Conta" icon="fa-user-circle" />
              </nav>
            </div>

            {/* User info e actions */}
            <div className="flex items-center gap-3">
              {/* Plano Atual - Destaque */}
              {user?.subscription && (
                <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-brand-500/10 border border-brand-500/20">
                  <i className="fa-solid fa-crown text-brand-400 text-sm"></i>
                  <span className="text-sm font-semibold">{user.subscription.planName}</span>
                </div>
              )}

              {/* Theme toggle */}
              <button
                onClick={toggle}
                className="w-9 h-9 rounded-lg hover:bg-[var(--cg-surface-alt)] flex items-center justify-center transition-colors"
                aria-label="Alternar tema"
                title={theme==='dark' ? 'Modo claro' : 'Modo escuro'}
              >
                <i className={`fa-solid ${theme==='dark'? 'fa-sun' : 'fa-moon'} text-sm`}></i>
              </button>

              {/* User menu desktop */}
              {user && (
                <div className="hidden md:flex items-center gap-3 pl-3 border-l" style={{borderColor:'var(--cg-border)'}}>
                  <div className="text-right">
                    <div className="text-xs font-medium max-w-[140px] truncate">{user.name || user.email}</div>
                    <div className="text-[10px] opacity-60">{user.plan?.name}</div>
                  </div>
                  <button
                    onClick={logout}
                    className="w-9 h-9 rounded-lg hover:bg-red-500/10 hover:text-red-500 flex items-center justify-center transition-colors"
                    title="Sair"
                  >
                    <i className="fa-solid fa-right-from-bracket text-sm"></i>
                  </button>
                </div>
              )}

              {/* Mobile menu button */}
              <button
                className="md:hidden w-9 h-9 rounded-lg hover:bg-[var(--cg-surface-alt)] flex items-center justify-center transition-colors"
                onClick={()=>setOpen(o=>!o)}
                aria-label="Menu"
              >
                <i className={`fa-solid ${open ? 'fa-times' : 'fa-bars'} text-sm`}></i>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="md:hidden border-t fade-in" style={{borderColor:'var(--cg-border)', background:'var(--cg-bg)'}}>
            <div className="px-4 py-4 space-y-1">
              <MobileNavLink to="/app/generate" label="Gerar" icon="fa-wand-magic-sparkles" onClick={()=>setOpen(false)} />
              <MobileNavLink to="/app/social-posts" label="Posts" icon="fa-images" onClick={()=>setOpen(false)} />
              <MobileNavLink to="/app/chat" label="Chat" icon="fa-comments" onClick={()=>setOpen(false)} />
              <MobileNavLink to="/app/account" label="Conta" icon="fa-user-circle" onClick={()=>setOpen(false)} />

              {user && (
                <>
                  <div className="h-px bg-[var(--cg-border)] my-3"></div>
                  <div className="px-3 py-2 text-xs">
                    <div className="font-medium mb-1">{user.name || user.email}</div>
                    {user.subscription && (
                      <div className="opacity-60">{user.subscription.planName}</div>
                    )}
                  </div>
                  <button
                    onClick={()=>{logout(); setOpen(false);}}
                    className="w-full text-left px-3 py-2 rounded-lg text-sm font-medium text-red-500 hover:bg-red-500/10 transition-colors flex items-center gap-2"
                  >
                    <i className="fa-solid fa-right-from-bracket"></i>
                    Sair
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1 w-full max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
        {children}
      </main>

      {/* Footer melhorado */}
      <footer className="border-t mt-auto" style={{borderColor:'var(--cg-border)', background:'var(--cg-surface)'}}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-brand-500 to-brand-700 flex items-center justify-center">
                <i className="fa-solid fa-wand-magic-sparkles text-white text-xs"></i>
              </div>
              <span className="font-bold">CopyGenius</span>
            </div>

            <div className="text-xs opacity-60">
              © {new Date().getFullYear()} CopyGenius • Copy profissional em segundos
            </div>

            <div className="flex items-center gap-4 text-xs">
              <Link to="/app/generate" className="hover:text-brand-400 transition-colors">Gerar</Link>
              <Link to="/app/chat" className="hover:text-brand-400 transition-colors">Chat</Link>
              <Link to="/app/account" className="hover:text-brand-400 transition-colors">Conta</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function NavLink({to,label,icon}:{to:string; label:string; icon?:string}){
  const location = useLocation();
  const isActive = location.pathname === to || (to !== '/app' && location.pathname.startsWith(to));

  return (
    <Link
      to={to}
      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
        isActive
          ? 'bg-brand-500/10 text-brand-400'
          : 'hover:bg-[var(--cg-surface-alt)]'
      }`}
    >
      {icon && <i className={`fa-solid ${icon} text-xs`}></i>}
      {label}
    </Link>
  );
}

function MobileNavLink({to,label,icon,onClick}:{to:string; label:string; icon?:string; onClick():void}){
  const location = useLocation();
  const isActive = location.pathname === to || (to !== '/app' && location.pathname.startsWith(to));

  return (
    <Link
      to={to}
      onClick={onClick}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
        isActive
          ? 'bg-brand-500/10 text-brand-400'
          : 'hover:bg-[var(--cg-surface-alt)]'
      }`}
    >
      {icon && <i className={`fa-solid ${icon}`}></i>}
      {label}
    </Link>
  );
}

function Card({title, children}:{title:string; children:React.ReactNode}){
  return (
    <div className="panel p-5 flex flex-col gap-3 fade-in">
      <h3 className="text-sm font-semibold tracking-wide" style={{color:'var(--cg-text)'}}>{title}</h3>
      {children}
    </div>
  );
}

function OverviewPage(){
  const { user } = useAuth();
  const firstName = user?.name?.split(' ')[0] || '';

  return (
    <div className="space-y-8">
      {/* Welcome header */}
      <div>
        <h1 className="text-3xl font-bold mb-2 heading">
          {firstName ? `Olá, ${firstName}! 👋` : 'Bem-vindo de volta! 👋'}
        </h1>
        <p className="text-base opacity-70">
          Pronto para criar copy que converte?
        </p>
      </div>

      {/* Novo Dashboard de Uso */}
      <UsageDashboard />

      {/* Quick actions */}
      <div>
        <h2 className="text-lg font-semibold mb-4 heading">Ações Rápidas</h2>
        <div className="grid gap-4 md:grid-cols-3">
          <QuickActionCard
            to="/app/generate"
            icon="fa-wand-magic-sparkles"
            title="Gerar Conteúdo"
            description="Crie copy profissional com E-commerce, Social Media ou Modo Livre"
            color="brand"
          />
          <QuickActionCard
            to="/app/chat"
            icon="fa-comments"
            title="Chat Conversacional"
            description="Converse com IA mantendo contexto e histórico completo"
            color="green"
          />
          <QuickActionCard
            to="/app/account"
            icon="fa-user-circle"
            title="Minha Conta"
            description="Gerencie seu plano e configurações"
            color="purple"
          />
        </div>
      </div>
    </div>
  );
}

function QuickActionCard({to, icon, title, description, color}: {to:string; icon:string; title:string; description:string; color:'brand'|'purple'|'green'}){
  const colors = {
    brand: 'from-brand-500 to-brand-600',
    purple: 'from-purple-500 to-purple-600',
    green: 'from-green-500 to-green-600'
  };

  return (
    <Link
      to={to}
      className="panel p-5 hover:scale-[1.02] transition-all group"
    >
      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colors[color]} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg`}>
        <i className={`fa-solid ${icon} text-white text-lg`}></i>
      </div>
      <h3 className="font-semibold mb-1 heading">{title}</h3>
      <p className="text-xs opacity-70 leading-relaxed">{description}</p>
    </Link>
  );
}

function PlanBadge({name,status}:{name?:string; status?:string}){
  if(!name) return null;
  const base = 'px-2 py-0.5 rounded text-[10px] font-medium tracking-wide inline-flex items-center gap-1';
  let color = 'bg-slate-800 text-slate-200 border border-slate-600';
  if(name === 'Pro') color = 'bg-gradient-to-r from-brand-600 to-brand-500 text-white shadow-glow';
  else if(name.toLowerCase().includes('teste')) color = 'bg-amber-500/20 text-amber-600 border border-amber-500/50';
  const statusIcon = status?.toLowerCase()==='inadimplente' ? <i className="fa-solid fa-triangle-exclamation text-amber-500"></i> : status?.toLowerCase()==='cancelada' ? <i className="fa-solid fa-ban text-red-400"></i> : null;
  return <span className={base + ' ' + color}>{statusIcon}{name}</span>;
}
